import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Search } from 'lucide-react';
import {
  EMOJI, EMOJI_CATEGORIES, QUICK_REACTIONS, emojiInfo, recentEmoji, rememberEmoji, searchEmoji,
} from '../utils/reactions';
import styles from '../Chat.module.css';

/**
 * Emoji picker popover — reactions and the composer share it.
 * Positioned by the caller through `style` (it is absolutely positioned).
 */
export default function EmojiPicker({ onPick, onClose, style, label = 'Emoji picker' }) {
  const [query, setQuery] = useState('');
  const [hovered, setHovered] = useState(null);
  const rootRef = useRef(null);
  const searchRef = useRef(null);

  // Focus the search without scrolling the conversation under the picker.
  useEffect(() => { searchRef.current?.focus({ preventScroll: true }); }, []);

  useEffect(() => {
    const onDown = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) onClose?.(); };
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose?.(); } };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [onClose]);

  const frequent = useMemo(() => {
    const recents = recentEmoji();
    return Array.from(new Set([...recents, ...QUICK_REACTIONS, '🙏', '🎉', '🔥'])).slice(0, 8);
  }, []);

  const pick = (char) => {
    rememberEmoji(char);
    onPick(char);
  };

  const results = query ? searchEmoji(query) : null;
  const shown = hovered ? emojiInfo(hovered) : null;

  const grid = (chars) => (
    <div className={styles.emojiGrid}>
      {chars.map((char) => {
        const info = emojiInfo(char);
        return (
          <button
            key={char}
            type="button"
            className={styles.emojiBtn}
            aria-label={info?.name || char}
            onClick={() => pick(char)}
            onMouseEnter={() => setHovered(char)}
            onFocus={() => setHovered(char)}
          >
            {char}
          </button>
        );
      })}
    </div>
  );

  return (
    <div ref={rootRef} role="dialog" aria-label={label} className={styles.emojiPicker} style={style}>
      <div className={styles.emojiSearch}>
        <label className={styles.searchWrap}>
          <span className={styles.srOnly}>Search emoji</span>
          <Search size={14} className={styles.searchIcon} style={{ left: 10 }} aria-hidden="true" />
          <input
            type="search"
            className={styles.searchInput}
            placeholder="Search emoji"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && results?.length) { e.preventDefault(); pick(results[0].char); }
            }}
            ref={searchRef}
          />
        </label>
      </div>
      <div className={styles.emojiScroll}>
        {results ? (
          results.length ? grid(results.map((e) => e.char)) : (
            <div className={styles.listNote} style={{ padding: '12px' }}>No emoji match “{query}”.</div>
          )
        ) : (
          <>
            <div className={styles.emojiSection}>Frequently used</div>
            {grid(frequent)}
            {EMOJI_CATEGORIES.map((cat) => (
              <React.Fragment key={cat}>
                <div className={styles.emojiSection}>{cat}</div>
                {grid(EMOJI.filter((e) => e.cat === cat).map((e) => e.char))}
              </React.Fragment>
            ))}
          </>
        )}
      </div>
      <div className={styles.emojiFooter} aria-live="polite">
        {shown ? (
          <>
            <span className={styles.emojiFooterChar}>{shown.char}</span>
            <strong>{shown.name}</strong>
          </>
        ) : 'Pick an emoji'}
      </div>
    </div>
  );
}
